import { useCallback, useEffect, useRef, useState } from "react";
import type { Recorder, Segment } from "./recorder";

// After a parcel closes its clip runs on this long: the label stuck on and the
// parcel taped are part of the evidence.
export const TAIL_MS = 15_000;

export type TailedRecorder = Recorder & {
  /** the parcel closed: its clip ends TAIL_MS from now, or when the next one starts */
  finishAfter: (orderId: string, externalId: string) => void;
  /** a closed parcel's clip is still running (its tail) */
  tailing: boolean;
};

/**
 * The desk's recorder with a tail. One camera, one clip at a time: starting
 * the next parcel (or leaving) first ends and keeps the closed parcel's clip,
 * where the plain recorder would drop it.
 */
export function useTailedRecorder(rec: Recorder, push: (orderId: string, externalId: string, seg: Segment) => void): TailedRecorder {
  const recRef = useRef(rec);
  const pushRef = useRef(push);
  useEffect(() => {
    recRef.current = rec;
    pushRef.current = push;
  });
  const pending = useRef<{ orderId: string; externalId: string; timer: number; done?: Promise<void> } | null>(null);
  const [tailing, setTailing] = useState(false);
  const flush = useCallback((): Promise<void> => {
    const p = pending.current;
    if (!p) return Promise.resolve();
    if (!p.done) {
      window.clearTimeout(p.timer);
      p.done = recRef.current
        .finish(p.orderId, p.externalId)
        .then((seg) => {
          if (seg) pushRef.current(p.orderId, p.externalId, seg);
        })
        .finally(() => {
          if (pending.current === p) {
            pending.current = null;
            setTailing(false);
          }
        });
    }
    return p.done;
  }, []);
  const finishAfter = useCallback(
    (orderId: string, externalId: string) => {
      const p = { orderId, externalId, timer: 0 };
      p.timer = window.setTimeout(() => void flush(), TAIL_MS);
      pending.current = p;
      setTailing(true);
    },
    [flush],
  );
  const start = useCallback(
    (label: string, orderId?: string) => {
      void flush().then(() => recRef.current.start(label, orderId));
    },
    [flush],
  );
  // A desk letting go (the order panel closing after its parcel) leaves a
  // running tail to its timer; anything else running is dropped.
  const discard = useCallback(() => {
    if (!pending.current) recRef.current.discard();
  }, []);
  // Leaving the page keeps the clip of the parcel just closed.
  useEffect(() => () => void flush(), [flush]);
  return { ...rec, start, discard, finishAfter, tailing };
}
